import React, { useState } from 'react';
import {
    Text, 
    Modal,
    DateInput
} from '@simplybenefits/sb-ui-library';
import { constants } from '@simplybenefits/sb-utils';
import * as dates from '@simplybenefits/sb-dates';

const BENEFIT_NAME_MAP = constants.BENEFIT_NAME_MAP;

const GROUPED_HEALTH_BENEFITS = [
    'drugs',
    'majorMedical',
    'paramedicals',
    'vision'
];

const dateLabelMap = {
    TERMINATE_BENEFIT: "Effective Date of Termination",
    REINSTATE_BENEFIT: "Effective Date of Reinstatement",
    WAIVE_PREMIUM: "Effective Date of Waiver",
    REINSTATE_PREMIUM: "Effective Date of Reinstatement",
    WAIVE_ALL_PREMIUMS: "Effective Date of Waiver",
    REINSTATE_ALL_PREMIUMS: "Effective Date of Reinstatement"
};

const modalLabelMap = {
    TERMINATE_BENEFIT: "Yes, Terminate Benefit",
    REINSTATE_BENEFIT: "Yes, Reinstate Benefit",
    WAIVE_PREMIUM: "Yes, Waive Premium",
    REINSTATE_PREMIUM: "Yes, Reinstate Premium",
    WAIVE_ALL_PREMIUMS: "Yes, Waive All Premiums",
    REINSTATE_ALL_PREMIUMS: "Yes, Reinstate All Premiums"
};

const confirmTitleMap = {
    TERMINATE_BENEFIT: "Confirm Benefit Termination",
    REINSTATE_BENEFIT: "Confirm Reinstatement of Benefit",
    WAIVE_PREMIUM: "Confirm Waiver of Premium",
    REINSTATE_PREMIUM: "Confirm Reinstatement of Premium",
    WAIVE_ALL_PREMIUMS: "Confirm Waiver of All Premiums",
    REINSTATE_ALL_PREMIUMS: "Confirm Reinstatement of All Premiums"
};

/**
 * Returns the modal title string for a given action and benefit name.
 * @param {string} action - The action key (e.g. "TERMINATE_BENEFIT")
 * @param {string} benefitName - The benefit identifier key
 * @returns {string}
 */
const getTitle = (action, benefitName) => {
    const titleMap = {
        TERMINATE_BENEFIT: `${ BENEFIT_NAME_MAP[benefitName] } Benefit Termination`,
        REINSTATE_BENEFIT: `${ BENEFIT_NAME_MAP[benefitName] } Reinstate Benefit`,
        WAIVE_PREMIUM: `Waive Premium for ${ BENEFIT_NAME_MAP[benefitName] }`,
        REINSTATE_PREMIUM: `Reinstate Premium for ${ BENEFIT_NAME_MAP[benefitName] }`,
        WAIVE_ALL_PREMIUMS: "Waive All Premiums",
        REINSTATE_ALL_PREMIUMS: "Reinstate All Premiums"
    };

    return titleMap[action];
};

/**
 * 
 * @param {*} isGroupedHealthBenefit 
 * @param {*} action 
 * @param {*} benefitName 
 * @returns 
 */
const getVerbiage = (isGroupedHealthBenefit, action, benefitName) => {
    const groupedBenefitVerbiage = `Updating the premium waiver for ${ BENEFIT_NAME_MAP[benefitName] } will also apply this change to the entire EHC bundle (Drugs, Health, Paramedicals, and Vision).`;

    const verbiageMap = {
        TERMINATE_BENEFIT: "If the effective date is set to be on the first of the month, then the billing for the terminated benefit will occur that month. If the effective date is set after the first of the month, then the billing for the terminated benefit will be applied to the following month.",
        REINSTATE_BENEFIT: "If the effective date is set to be on the first of the month, then the billing for the reinstated benefit will occur that month. If the effective date is set after the first of the month, then the billing for the reinstated benefit will be applied to the following month.",
        WAIVE_PREMIUM: `${ isGroupedHealthBenefit ? groupedBenefitVerbiage : '' } If the effective date is set to the first of the month, the waived premium will take effect immediately. If set after the first, the waived premium will apply starting the following month. Waiving the premium will stop billing for this benefit but does not impact the member’s eligibility or coverage. Backdated changes will have adjustments applied using current rates.`,
        REINSTATE_PREMIUM: `${ isGroupedHealthBenefit ? groupedBenefitVerbiage : '' } If the effective date is set to the first of the month, the premium will resume immediately. If set after the first, the premium will resume in the following month. Reinstating the premium will resume billing for this benefit but does not impact the member’s eligibility or coverage. Backdated changes will have adjustments applied using current rates.`,
        WAIVE_ALL_PREMIUMS: "If the effective date is set to the first of the month, the waived premium will take effect immediately. If set after the first, the waived premium will apply starting the following month. Waiving the premium will stop billing for this benefit but does not impact the member’s eligibility or coverage. Backdated changes will have adjustments applied using current rates.",
        REINSTATE_ALL_PREMIUMS: "If the effective date is set to the first of the month, the premium will resume immediately. If set after the first, the premium will resume in the following month. Reinstating the premium will resume billing for this benefit but does not impact the member’s eligibility or coverage. Backdated changes will have adjustments applied using current rates."
    };

    return verbiageMap[action];
};

/**
 * Modal for performing benefit actions (terminate, reinstate, waive/reinstate premium).
 * Shows a two-step flow: effective date entry followed by a confirmation screen.
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm
 * @param {string} props.effectiveDate
 * @param {Function} props.setEffectiveDate
 * @param {string} props.effectiveDateError
 * @param {Function} props.validateEffectiveDate
 * @param {string} props.action
 * @param {string} props.selectedBenefitName
 * @param {object} props.user
 * @returns {JSX.Element}
 */
const BenefitActionModal = ({
    open,
    onClose,
    onConfirm,
    effectiveDate,
    setEffectiveDate,
    effectiveDateError,
    validateEffectiveDate,
    action,
    selectedBenefitName,
    user
}) => {
    const [ isDateStep, setIsDateStep ] = useState(true);
    const title = isDateStep 
        ? getTitle(action, selectedBenefitName)
        : confirmTitleMap[action];

    const dateLabel = dateLabelMap[action];
    const modalLabel = modalLabelMap[action];
    const isGroupedHealthBenefit = GROUPED_HEALTH_BENEFITS.includes(selectedBenefitName);
    const verbiage = getVerbiage(isGroupedHealthBenefit, action, selectedBenefitName);

    /**
     * Do the effective date validation here, then continue to the confirmation if the date is valid 
     */
    const validateAndContinueToNextStep = async () => {
        const effectiveDateValid = await validateEffectiveDate(effectiveDate);

        if (effectiveDateValid) {
            setIsDateStep(false);
        }
    };
   
    /**
     * Return the verbiage to be used in the confirmation modal.
     * @returns {JSX.Element}
     */
    const getConfirmationVerbiage = () => {
        switch (action) {
            case "TERMINATE_BENEFIT":
                return (
                    <Text medium customStyle={{ paddingLeft: 0 }}>
                        Are you sure you want to terminate the member { `${ user.firstname } ${ user.lastname }'s` } <Text medium bold customStyle={{ display: "inline" }}>{ BENEFIT_NAME_MAP[selectedBenefitName] }</Text> benefit? 
                        This change will be effective <Text medium bold customStyle={{ display: "inline" }}>{ dates.getLocalDisplay(effectiveDate, 'll') }</Text>.
                    </Text>
                );
    
            case "REINSTATE_BENEFIT":
                return (
                    <Text medium customStyle={{ paddingLeft: 0 }}>
                        Are you sure you want to activate the member { `${ user.firstname } ${ user.lastname }'s` } <Text medium bold customStyle={{ display: "inline" }}>{ BENEFIT_NAME_MAP[selectedBenefitName] }</Text> benefit? 
                        This change will be effective <Text medium bold customStyle={{ display: "inline" }}>{ dates.getLocalDisplay(effectiveDate, 'll') }</Text>.
                    </Text>
                );

            case "WAIVE_PREMIUM":
                return (
                    <Text medium customStyle={{ paddingLeft: 0 }}>
                        Are you sure you want to waive the premium for <Text medium bold customStyle={{ display: "inline" }}>{ BENEFIT_NAME_MAP[selectedBenefitName] }</Text>?
                        This change will be effective <Text medium bold customStyle={{ display: "inline" }}>{ dates.getLocalDisplay(effectiveDate, 'll') }</Text>.
                        This action will stop billing for the benefit but does not impact eligibility.
                    </Text>
                );

            case "REINSTATE_PREMIUM":
                return (
                    <Text medium customStyle={{ paddingLeft: 0 }}>
                        Are you sure you want to reinstate the premium for <Text medium bold customStyle={{ display: "inline" }}>{ BENEFIT_NAME_MAP[selectedBenefitName] }</Text>?
                        This change will be effective <Text medium bold customStyle={{ display: "inline" }}>{ dates.getLocalDisplay(effectiveDate, 'll') }</Text>.
                        This action will resume billing for the benefit but does not impact eligibility.
                    </Text>
                );

            case "WAIVE_ALL_PREMIUMS":
                return (
                    <Text medium customStyle={{ paddingLeft: 0 }}>
                        Are you sure you want to waive the premiums for all benefits (in “Active” and “Not Eligible” status) for this member?
                        This change will be effective <Text medium bold customStyle={{ display: "inline" }}>{ dates.getLocalDisplay(effectiveDate, 'll') }</Text>.
                        This action will stop billing for the benefit but does not impact eligibility.
                    </Text>
                );

            case "REINSTATE_ALL_PREMIUMS":
                return (
                    <Text medium customStyle={{ paddingLeft: 0 }}>
                        Are you sure you want to reinstate all benefit premiums for this member?
                        This change will be effective <Text medium bold customStyle={{ display: "inline" }}>{ dates.getLocalDisplay(effectiveDate, 'll') }</Text>.
                        This action will resume billing for the benefit but does not impact eligibility.
                    </Text>
                );
    
            default:
                return null;
        }
    };

    /**
     * 
     */
    const handleConfirm = async () => {
        const success = await onConfirm();
        if (success) {
            setIsDateStep(true);
        }
    };

    return (
        <Modal
            smallTitle={true}
            open={open}
            onClose={onClose}
            title={title}
            primaryButton={{
                label: isDateStep ? 'Next' : modalLabel,
                onClick: isDateStep ? validateAndContinueToNextStep : handleConfirm
            }}
        >
            {
                isDateStep
                    ? (
                        <>
                            <Text medium customStyle={{ paddingLeft: 0 }}>
                                { verbiage } 
                            </Text>

                            <DateInput
                                label={dateLabel}
                                value={effectiveDate}
                                variant="outlined"
                                onChange={(e) => setEffectiveDate(e.target.value)}
                                required
                                fullWidth
                                customStyle={{ marginTop: "20px", marginBottom: "10px" }}
                                error={Boolean(effectiveDateError)}
                                helperText={effectiveDateError}
                            />
                        </>
                    )
                    : 
                    getConfirmationVerbiage()
            }
       
        </Modal>
    );
};

export default BenefitActionModal;